// ---------------------------------------------------------------------------
// flux_classifier.ts  —  Phase 3: Flux-Signature Spell Templates
//
// Replaces shape-matching classification.  Spells are matched against the
// accumulated flux state of the strand graph — endpoint flux, loop topology,
// winding signs, and standing trances.
//
// Pure functions.  No rendering, no state.
// ---------------------------------------------------------------------------

import type { StrandGraph, Loop, StrandNode, LoopScale } from './strand_graph.js';
import { getEndpoints, getCrossingNodes, isRectangularLoop, pointInLoop } from './strand_graph.js';
import type { FluxSignature } from './flux_engine.js';
import type { BlockId } from '../shared/notebook_model.js';

// ── Public types ─────────────────────────────────────────────────────────────

export interface FluxClassificationResult {
  spellId: string;
  confidence: number;
  parameters: Record<string, unknown>;
}

export type BlockHitTest = (x: number, y: number) => { id: BlockId; kind: string } | null;

// ── Template interface ───────────────────────────────────────────────────────

interface FluxTemplate {
  id: string;
  match(
    g: StrandGraph,
    sig: FluxSignature,
    hitTest: BlockHitTest,
  ): FluxClassificationResult | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function topEndpoint(g: StrandGraph, sign: 'positive' | 'negative'): StrandNode | null {
  const endpoints = getEndpoints(g);
  let best: StrandNode | null = null;
  let bestFlux = 0;
  for (const ep of endpoints) {
    const f = ep.absorbedFlux;
    if (sign === 'positive' && f > bestFlux) { best = ep; bestFlux = f; }
    if (sign === 'negative' && f < bestFlux) { best = ep; bestFlux = f; }
  }
  return best;
}

function loopsByScale(g: StrandGraph, scale: LoopScale): Loop[] {
  return g.loops.filter(l => l.scale === scale && l.active);
}

function grandLoops(g: StrandGraph): Loop[] {
  return g.loops.filter(l => (l.scale === 'grand' || l.scale === 'major') && l.active);
}

function anyActiveLoop(g: StrandGraph): Loop | null {
  return g.loops.find(l => l.active) ?? null;
}

function loopBbox(g: StrandGraph, loop: Loop): { x: number; y: number; width: number; height: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const sid of loop.strands) {
    const s = g.strands.get(sid);
    if (!s) continue;
    for (const p of s.points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function totalPositiveFlux(sig: FluxSignature): number {
  let sum = 0;
  for (const [, f] of sig.endpoints) {
    if (f > 0) sum += f;
  }
  return sum;
}

function totalNegativeFlux(sig: FluxSignature): number {
  let sum = 0;
  for (const [, f] of sig.endpoints) {
    if (f < 0) sum += f;
  }
  return sum;
}

function totalAbsFlux(sig: FluxSignature): number {
  let sum = 0;
  for (const [, f] of sig.endpoints) {
    sum += Math.abs(f);
  }
  return sum;
}

// ── Templates ────────────────────────────────────────────────────────────────
//
// The active spell vocabulary is intentionally minimal:
//
//   select.region — closed-or-near-closed lasso → select content inside.
//   erase.region  — closed lasso with negative flux (CW direction) → erase
//                   content inside.
//   erase.line    — single line/arc strand → erase whatever it crosses.
//
// Earlier experimental templates (derive.step, annotate.link, copy.region,
// paste.here, arcane.open, block.duplicate, snapshot.region, arcane.eval,
// astral.frame, layer.up/down, select.point) are intentionally disabled —
// they were aspirational gestures whose UX never settled in.  The code
// stays in this file for future revival but is excluded from `TEMPLATES`.
const TEMPLATES: FluxTemplate[] = [

  // ── select.region: closed-or-near-closed lasso ─────────────────────────
  // First branch: the strict "perfect CCW loop" — flux engine detected an
  // actual closed loop topologically.  Either winding is accepted now;
  // requiring CCW was forcing users to draw the lasso a specific direction
  // for no good reason.
  // Second branch: a "loose" closure — single strand, no detected loop,
  // but the endpoints are within 25 % of the arc length of each other
  // and the arc is long enough to plausibly enclose something.  This is
  // what makes "the lasso doesn't need to be perfectly closed" work.
  {
    id: 'select.region',
    match(g, sig, hitTest) {
      // Strict: 1 dominant grand loop
      const loops = grandLoops(g);
      if (loops.length === 1) {
        const loop = loops[0];
        const nonLoopStrands = g.strands.size - loop.strands.length;
        if (nonLoopStrands <= 2) {
          const bbox = loopBbox(g, loop);
          return {
            spellId: 'select.region',
            confidence: 0.92,
            parameters: {
              region: { bbox, points: [] as Array<{x:number;y:number}> },
              loopCenter: loop.center,
              loopArea: loop.area,
              loopWinding: loop.winding,
            },
          };
        }
      }
      // Loose: single open strand whose endpoints almost meet
      if (g.loops.length === 0 && g.strands.size === 1) {
        const strand = g.strands.values().next().value;
        if (strand && strand.arcLength >= 200) {
          const pts = strand.points;
          const a = pts[0], b = pts[pts.length - 1];
          const gap = Math.hypot(b.x - a.x, b.y - a.y);
          if (gap <= strand.arcLength * 0.25) {
            // Bbox from points
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            for (const p of pts) {
              if (p.x < minX) minX = p.x;
              if (p.x > maxX) maxX = p.x;
              if (p.y < minY) minY = p.y;
              if (p.y > maxY) maxY = p.y;
            }
            const bbox = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
            const cx = bbox.x + bbox.width / 2;
            const cy = bbox.y + bbox.height / 2;
            // Decimate the strand to ≤64 vertices for the inside-test polygon
            // — the raw stroke can have hundreds, and pointInPolygon is O(n)
            // per block / stroke.  64 is plenty to capture a hand-drawn lasso.
            const polyMax = 64;
            const polygon: Array<{ x: number; y: number }> = [];
            const step = Math.max(1, Math.floor(pts.length / polyMax));
            for (let i = 0; i < pts.length; i += step) {
              polygon.push({ x: pts[i].x, y: pts[i].y });
            }
            // Close the polygon by adding the first point at the end so the
            // ray-cast crosses the closing edge correctly.
            if (polygon.length > 0) polygon.push({ x: polygon[0].x, y: polygon[0].y });
            return {
              spellId: 'select.region',
              confidence: 0.85,
              parameters: {
                region: { bbox, points: polygon },
                loopCenter: { x: cx, y: cy },
                loopArea: bbox.width * bbox.height,
                loopWinding: 1,
              },
            };
          }
        }
      }
      return null;
    },
  },

  /* DISABLED — see vocabulary note above.
  // ── select.point: tap on a block (minimal structure) ─────────────────
  {
    id: 'select.point',
    match(g, sig, hitTest) {
      // Very small graph — 1 strand, 2 endpoints, no loops
      if (g.loops.length > 0) return null;
      if (g.strands.size > 1) return null;
      const eps = getEndpoints(g);
      if (eps.length !== 2) return null;
      // Check arc length is short
      const strand = g.strands.values().next().value;
      if (!strand || strand.arcLength > 80) return null;
      const mid = strand.points[Math.floor(strand.points.length / 2)];
      const hit = hitTest(mid.x, mid.y);
      if (!hit) return null;
      return {
        spellId: 'select.point',
        confidence: 0.85,
        parameters: { targetBlockId: hit.id, x: mid.x, y: mid.y },
      };
    },
  },

  // ── astral.frame: rectangular grand loop ─────────────────────────────
  {
    id: 'astral.frame',
    match(g, sig, hitTest) {
      const loops = grandLoops(g);
      if (loops.length !== 1) return null;
      const loop = loops[0];
      if (!isRectangularLoop(g, loop)) return null;

      const bbox = loopBbox(g, loop);
      return {
        spellId: 'astral.frame',
        confidence: 0.92,
        parameters: {
          regionBbox: bbox,
          loopCenter: loop.center,
          loopArea: loop.area,
        },
      };
    },
  },

  // ── layer.up: line + CCW knot above start ────────────────────────────
  {
    id: 'layer.up',
    match(g, sig, hitTest) {
      // Need at least one loop with positive winding
      const ccwLoops = g.loops.filter(l => l.winding === 1 && l.active);
      if (ccwLoops.length === 0) return null;
      const loop = ccwLoops[0];

      // Positive flux must dominate
      const posFlux = totalPositiveFlux(sig);
      if (posFlux < 0.5) return null;

      // Loop should be above or at the level of the start point
      // (the line goes upward into a CCW knot)
      const eps = getEndpoints(g);
      if (eps.length < 1) return null;
      // Find lowest endpoint (highest Y in screen coords)
      let lowestEp: StrandNode | null = null;
      for (const ep of eps) {
        if (!lowestEp || ep.y > lowestEp.y) lowestEp = ep;
      }
      if (!lowestEp) return null;

      // Loop center should be above the lowest endpoint
      if (loop.center.y > lowestEp.y) return null;

      // Hit test on the lowest endpoint (should be on a block)
      const hit = hitTest(lowestEp.x, lowestEp.y);
      if (!hit) return null;

      return {
        spellId: 'layer.up',
        confidence: 0.88,
        parameters: {
          targetBlockId: hit.id,
          direction: 'up',
          flux: posFlux,
        },
      };
    },
  },

  // ── layer.down: line + CW knot below start ──────────────────────────
  {
    id: 'layer.down',
    match(g, sig, hitTest) {
      const cwLoops = g.loops.filter(l => l.winding === -1 && l.active);
      if (cwLoops.length === 0) return null;
      const loop = cwLoops[0];

      const negFlux = totalNegativeFlux(sig);
      if (negFlux > -0.5) return null;

      const eps = getEndpoints(g);
      if (eps.length < 1) return null;
      let highestEp: StrandNode | null = null;
      for (const ep of eps) {
        if (!highestEp || ep.y < highestEp.y) highestEp = ep;
      }
      if (!highestEp) return null;

      // Loop center should be below the highest endpoint
      if (loop.center.y < highestEp.y) return null;

      const hit = hitTest(highestEp.x, highestEp.y);
      if (!hit) return null;

      return {
        spellId: 'layer.down',
        confidence: 0.88,
        parameters: {
          targetBlockId: hit.id,
          direction: 'down',
          flux: negFlux,
        },
      };
    },
  },

  */
  // ── erase.region: closed lasso with negative flux (CW direction) ──────
  {
    id: 'erase.region',
    match(g, sig, hitTest) {
      // Needs negative flux (CW loop or negative endpoint accumulation)
      const negFlux = totalNegativeFlux(sig);
      if (negFlux > -0.3) return null;

      // Should have at least one loop
      const loop = anyActiveLoop(g);
      if (!loop) return null;

      const bbox = loopBbox(g, loop);
      return {
        spellId: 'erase.region',
        confidence: 0.82,
        parameters: {
          region: { bbox, points: [] as Array<{x:number;y:number}> },
          flux: negFlux,
        },
      };
    },
  },

  // ── erase.line: single straight strand, no loops, negative context ───
  {
    id: 'erase.line',
    match(g, sig, hitTest) {
      if (g.loops.length > 0) return null;
      if (g.strands.size !== 1) return null;
      const strand = g.strands.values().next().value;
      if (!strand) return null;
      // Accept line or arc (erase swipe can be curved)
      if (strand.kind !== 'line' && strand.kind !== 'arc') return null;
      if (strand.arcLength < 40) return null;

      const first = strand.points[0];
      const last = strand.points[strand.points.length - 1];
      const bbox = {
        x: Math.min(first.x, last.x),
        y: Math.min(first.y, last.y),
        width: Math.abs(last.x - first.x),
        height: Math.abs(last.y - first.y),
      };

      return {
        spellId: 'erase.line',
        // Last-resort confidence: select.region (0.92) and erase.region
        // (0.82) both win when their own conditions match.  This rank
        // makes erase.line a true "discard option" when nothing more
        // specific claims the stroke.
        confidence: 0.55,
        parameters: {
          lineStart: { x: first.x, y: first.y },
          lineEnd: { x: last.x, y: last.y },
          lineBbox: bbox,
        },
      };
    },
  },

  /* DISABLED — see vocabulary note at top.
  // ── copy.region: selection active + positive check-like topology ──────
  {
    id: 'copy.region',
    match(g, sig, hitTest) {
      const posFlux = totalPositiveFlux(sig);
      if (posFlux < 0.5) return null;

      // Need crossing nodes (the checkmark creates a junction or knot)
      const crossings = getCrossingNodes(g);
      if (crossings.length < 1) return null;

      // Should have at least one loop
      const loop = anyActiveLoop(g);
      if (!loop) return null;

      const bbox = loopBbox(g, loop);
      return {
        spellId: 'copy.region',
        confidence: 0.80,
        parameters: {
          regionBbox: bbox,
          flux: posFlux,
        },
      };
    },
  },

  // ── paste.here: X junction — opposing flux cancellation ──────────────
  {
    id: 'paste.here',
    match(g, sig, hitTest) {
      // Need exactly 1 junction/knot, and flux near zero (cancellation)
      const crossings = getCrossingNodes(g);
      if (crossings.length !== 1) return null;

      const absFlux = totalAbsFlux(sig);
      // Cancellation: total absorbed flux should be low (opposing streams met)
      if (absFlux > 3.0) return null;

      const junction = crossings[0];
      // Should not be on a block
      const hit = hitTest(junction.x, junction.y);
      if (hit) return null;

      return {
        spellId: 'paste.here',
        confidence: 0.78,
        parameters: {
          targetX: junction.x,
          targetY: junction.y,
        },
      };
    },
  },

  // ── arcane.open: spiral (high winding, monotonic turning) ────────────
  {
    id: 'arcane.open',
    match(g, sig, hitTest) {
      // Multiple loops stacked (spiral creates nested loops)
      if (g.loops.length < 2) return null;

      // All loops same winding (consistent spiral direction)
      const sign = g.loops[0].winding;
      if (!g.loops.every(l => l.winding === sign)) return null;

      // High total flux
      const totalFlux = totalAbsFlux(sig);
      if (totalFlux < 2.0) return null;

      return {
        spellId: 'arcane.open',
        confidence: 0.85,
        parameters: {
          center: g.loops[0].center,
          winding: sign,
          loopCount: g.loops.length,
        },
      };
    },
  },

  // ── block.duplicate: figure-8 (two loops, opposite winding) ──────────
  {
    id: 'block.duplicate',
    match(g, sig, hitTest) {
      if (g.loops.length !== 2) return null;
      // Opposite winding = figure-8
      if (g.loops[0].winding === g.loops[1].winding) return null;

      const center = {
        x: (g.loops[0].center.x + g.loops[1].center.x) / 2,
        y: (g.loops[0].center.y + g.loops[1].center.y) / 2,
      };
      const hit = hitTest(center.x, center.y);
      if (!hit) return null;

      return {
        spellId: 'block.duplicate',
        confidence: 0.82,
        parameters: { targetBlockId: hit.id, center },
      };
    },
  },

  // ── snapshot.region: loop + standing trance ──────────────────────────
  {
    id: 'snapshot.region',
    match(g, sig, hitTest) {
      if (sig.trances.length === 0) return null;
      const loop = anyActiveLoop(g);
      if (!loop) return null;
      if (loop.scale !== 'grand' && loop.scale !== 'major') return null;

      const bbox = loopBbox(g, loop);
      return {
        spellId: 'snapshot.region',
        confidence: 0.80,
        parameters: { regionBbox: bbox },
      };
    },
  },

  // ── arcane.eval: loop + rune strands inside (no blocks inside) ───────
  {
    id: 'arcane.eval',
    match(g, sig, hitTest) {
      const loops = grandLoops(g);
      if (loops.length < 1) return null;
      const loop = loops[0];

      // Must have extra strands beyond the loop itself
      const extraStrands = g.strands.size - loop.strands.length;
      if (extraStrands < 1) return null;

      // No blocks inside the loop
      const bbox = loopBbox(g, loop);
      const cx = bbox.x + bbox.width / 2;
      const cy = bbox.y + bbox.height / 2;
      if (hitTest(cx, cy)) return null;

      return {
        spellId: 'arcane.eval',
        confidence: 0.78,
        parameters: {
          regionBbox: bbox,
          loopCenter: loop.center,
          runeStrandCount: extraStrands,
        },
      };
    },
  },

  // ── derive.step: loop + downward tail ────────────────────────────────
  {
    id: 'derive.step',
    match(g, sig, hitTest) {
      const loops = grandLoops(g);
      if (loops.length < 1) return null;
      const loop = loops[0];

      // Need an endpoint below the loop (downward tail)
      const eps = getEndpoints(g);
      let tailEp: StrandNode | null = null;
      for (const ep of eps) {
        if (ep.y > loop.center.y + 20) {
          if (!tailEp || ep.y > tailEp.y) tailEp = ep;
        }
      }
      if (!tailEp) return null;

      // Hit test at loop center
      const hit = hitTest(loop.center.x, loop.center.y);

      const bbox = loopBbox(g, loop);
      return {
        spellId: 'derive.step',
        confidence: 0.80,
        parameters: {
          regionBbox: bbox,
          targetBlockId: hit?.id ?? null,
          loopCenter: loop.center,
        },
      };
    },
  },

  // ── annotate.link: single straight strand connecting two blocks ──────
  {
    id: 'annotate.link',
    match(g, sig, hitTest) {
      if (g.loops.length > 0) return null;
      if (g.strands.size !== 1) return null;
      const strand = g.strands.values().next().value;
      if (!strand || strand.kind !== 'line') return null;
      if (strand.arcLength < 30) return null;

      const first = strand.points[0];
      const last = strand.points[strand.points.length - 1];
      const hitA = hitTest(first.x, first.y);
      const hitB = hitTest(last.x, last.y);
      if (!hitA || !hitB) return null;
      if (hitA.id === hitB.id) return null;

      return {
        spellId: 'annotate.link',
        confidence: 0.82,
        parameters: {
          fromBlockId: hitA.id,
          toBlockId: hitB.id,
        },
      };
    },
  },
  */
];

// ── Main entry ───────────────────────────────────────────────────────────────

/**
 * Classify a spell from the flux state of a strand graph.
 * Returns the highest-confidence match, or null if nothing qualifies.
 */
export function classifyFromFlux(
  g: StrandGraph,
  sig: FluxSignature,
  hitTest: BlockHitTest,
): FluxClassificationResult | null {
  let best: FluxClassificationResult | null = null;
  for (const tmpl of TEMPLATES) {
    const r = tmpl.match(g, sig, hitTest);
    if (r && (!best || r.confidence > best.confidence)) {
      best = r;
    }
  }
  return best;
}
